/**
 * Barcode Scanner Dialog Component
 *
 * Dialog for scanning barcodes using device camera or manual input.
 * Supports EAN-13, EAN-8, UPC, and Code-128 formats.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Tab,
  Tabs,
  Paper,
  CircularProgress,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface BarcodeScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
  title?: string;
}

type ScanMode = 'camera' | 'manual';

function isBarcodeDetectorAvailable(): boolean {
  return typeof window !== 'undefined' && 'BarcodeDetector' in window;
}

export function BarcodeScannerDialog({
  open,
  onClose,
  onScan,
  title,
}: BarcodeScannerDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const [mode, setMode] = useState<ScanMode>(() =>
    isBarcodeDetectorAvailable() ? 'camera' : 'manual',
  );
  const [manualInput, setManualInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraAvailable, setCameraAvailable] = useState(() => isBarcodeDetectorAvailable());
  const [lastScanned, setLastScanned] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<any>(null);
  const scanIntervalRef = useRef<number | null>(null);
  const handleBarcodeDetectedRef = useRef<(barcode: string) => void>(() => undefined);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    if (scanIntervalRef.current !== null) {
      cancelAnimationFrame(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    detectorRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    if (!isBarcodeDetectorAvailable()) {
      setMode('manual');
      setCameraAvailable(false);
      setScanning(false);
      return;
    }

    try {
      setScanning(true);
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      const detector = new (window as any).BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'],
      });
      detectorRef.current = detector;

      const scanFrame = async () => {
        if (!videoRef.current || !streamRef.current || !detectorRef.current) return;
        try {
          const barcodes = await detectorRef.current.detect(videoRef.current);
          if (barcodes.length > 0) {
            handleBarcodeDetectedRef.current(barcodes[0].rawValue);
            return;
          }
        } catch {
          // Detection failed for this frame, continue scanning
        }
        scanIntervalRef.current = requestAnimationFrame(scanFrame);
      };
      scanIntervalRef.current = requestAnimationFrame(scanFrame);

      setScanning(false);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError(t('barcode.cameraFailed'));
      setScanning(false);
      setCameraAvailable(false);
      setMode('manual');
    }
  }, [t]);

  useEffect(() => {
    if (!open) return;

    if (!isBarcodeDetectorAvailable()) {
      setMode('manual');
      setCameraAvailable(false);
      return;
    }

    const checkCamera = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some((device) => device.kind === 'videoinput');
        setCameraAvailable(hasCamera);
        if (!hasCamera) {
          setMode('manual');
          setError(t('barcode.cameraNotFound'));
        }
      } catch (err) {
        console.error('Error checking camera:', err);
        setCameraAvailable(false);
        setMode('manual');
        setError(t('barcode.cameraPermissionDenied'));
      }
    };

    void checkCamera();
  }, [open, t]);

  useEffect(() => {
    if (!open || mode !== 'camera' || !cameraAvailable || !isBarcodeDetectorAvailable()) {
      stopCamera();
      return;
    }

    void startCamera();

    return () => {
      stopCamera();
    };
  }, [open, mode, cameraAvailable, startCamera, stopCamera]);

  const handleManualSubmit = () => {
    const trimmed = manualInput.trim();
    if (trimmed) {
      handleBarcodeDetected(trimmed);
    }
  };

  const handleBarcodeDetected = (barcode: string) => {
    setLastScanned(barcode);

    setTimeout(() => {
      onScan(barcode);
      doClose();
    }, 500);
  };
  handleBarcodeDetectedRef.current = handleBarcodeDetected;

  const doClose = () => {
    stopCamera();
    setManualInput('');
    setError(null);
    setLastScanned(null);
    setScanning(false);
    onClose();
  };

  const handleClose = () => {
    const isDirty = mode === 'manual' && manualInput.trim().length > 0;
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      doClose();
    }
  };

  const handleModeChange = (_: React.SyntheticEvent, newMode: ScanMode) => {
    setMode(newMode);
    setError(null);
    setLastScanned(null);
  };

  return (
    <>
      <ResponsiveDialog mobileTitle={t('barcode.scanTitle')} open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCodeScannerIcon />
              <Typography variant="h6">{title || t('barcode.scanTitle')}</Typography>
            </Box>
            <Tooltip title={tc('close')}>
              <IconButton onClick={handleClose} size="small" aria-label={tc('close')}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={mode} onChange={handleModeChange}>
            <Tab
              value="camera"
              icon={<CameraAltIcon />}
              label={t('barcode.cameraTab')}
              iconPosition="start"
              disabled={!cameraAvailable}
            />
            <Tab value="manual" icon={<KeyboardIcon />} label={t('barcode.manualTab')} iconPosition="start" />
          </Tabs>
        </Box>

        <DialogContent sx={{ minHeight: 400 }}>
          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {lastScanned && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">{t('barcode.barcodeScanned')}</Typography>
              <Typography variant="h6" fontFamily="monospace">
                {lastScanned}
              </Typography>
            </Alert>
          )}

          {mode === 'camera' && (
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 300,
                  bgcolor: 'black',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {scanning && (
                  <Box
                    sx={{
                      position: 'absolute',
                      zIndex: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <CircularProgress sx={{ color: 'white' }} />
                    <Typography color="white">{t('barcode.startingCamera')}</Typography>
                  </Box>
                )}

                <video
                  ref={videoRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  playsInline
                  muted
                />

                {!scanning && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '80%',
                      height: 80,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      '&::before, &::after': {
                        content: '""',
                        position: 'absolute',
                        width: 20,
                        height: 20,
                        border: '3px solid',
                        borderColor: 'primary.main',
                      },
                      '&::before': {
                        top: -2,
                        left: -2,
                        borderRight: 'none',
                        borderBottom: 'none',
                      },
                      '&::after': {
                        bottom: -2,
                        right: -2,
                        borderLeft: 'none',
                        borderTop: 'none',
                      },
                    }}
                  />
                )}
              </Paper>

              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                {t('barcode.pointAtCamera')}
              </Typography>
            </Box>
          )}

          {mode === 'manual' && (
            <Box>
              {!isBarcodeDetectorAvailable() && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('barcode.cameraScanUnsupported')}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('barcode.enterManually')}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label={t('barcode.barcodeLabel')}
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder={t('barcode.barcodePlaceholder')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleManualSubmit();
                  }
                }}
                sx={{ mt: 2 }}
                slotProps={{
                  input: {
                    sx: { fontFamily: 'monospace', fontSize: '1.1rem' },
                  },
                }}
              />

              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  {t('barcode.supportedTypes')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {['EAN-13', 'EAN-8', 'UPC-A', 'UPC-E', 'Code-128', 'Code-39'].map((type) => (
                    <Paper
                      key={type}
                      variant="outlined"
                      sx={{ px: 1.5, py: 0.5, bgcolor: 'action.hover' }}
                    >
                      <Typography variant="caption">{type}</Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            {tc('cancel')}
          </Button>
          {mode === 'manual' && (
            <Button
              variant="contained"
              onClick={handleManualSubmit}
              disabled={!manualInput.trim()}
            >
              {t('barcode.useCode')}
            </Button>
          )}
        </DialogActions>
      </ResponsiveDialog>

      <ConfirmDialog
        open={showDiscardConfirm}
        title={tc('unsavedChanges')}
        message={tc('unsavedChangesMessage')}
        confirmText={tc('discard')}
        confirmColor="error"
        onConfirm={() => {
          setShowDiscardConfirm(false);
          doClose();
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  );
}
